import React, { useState, useEffect } from 'react';
import { Modal, Form, Alert, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useAddBloodPressureReadingMutation } from '#store';

const BP_SYSTOLIC_MIN = 50;
const BP_SYSTOLIC_MAX = 280;
const BP_DIASTOLIC_MIN = 30;
const BP_DIASTOLIC_MAX = 150;

function isPlausibleBloodPressure(systolic, diastolic) {
  return (
    systolic >= BP_SYSTOLIC_MIN &&
    systolic <= BP_SYSTOLIC_MAX &&
    diastolic >= BP_DIASTOLIC_MIN &&
    diastolic <= BP_DIASTOLIC_MAX
  );
}

export function AddBloodPressureModal({ show, onHide, onSuccess }) {
  const { t, i18n } = useTranslation();
  const today = moment().format('YYYY-MM-DD');
  const [addReading, { isLoading }] = useAddBloodPressureReadingMutation();
  const [date, setDate] = useState(today);
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [error, setError] = useState(null);
  const [rangeWarning, setRangeWarning] = useState(false);

  const isPl = i18n.language?.startsWith('pl');
  const dateDisplay = moment(date).format(isPl ? 'DD.MM.YYYY' : 'MMM D, YYYY');

  useEffect(() => {
    if (show) {
      setDate(moment().format('YYYY-MM-DD'));
      setRangeWarning(false);
      setError(null);
    }
  }, [show]);

  useEffect(() => {
    setRangeWarning(false);
  }, [systolic, diastolic]);

  const onlyDigits = (raw) => String(raw ?? '').replace(/\D/g, '');

  const doSave = async (sys, dia) => {
    setError(null);
    try {
      await addReading({
        date,
        systolic: sys,
        diastolic: dia,
      }).unwrap();
      setSystolic('');
      setDiastolic('');
      setRangeWarning(false);
      setDate(moment().format('YYYY-MM-DD'));
      onHide();
      await (onSuccess && onSuccess());
    } catch {
      setError(t('dashboard.blood-pressure-add-save-error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const sysStr = onlyDigits(systolic);
    const diaStr = onlyDigits(diastolic);

    if (!sysStr || !diaStr) {
      setError(t('dashboard.blood-pressure-add-error-both-required'));
      return;
    }

    const sys = parseInt(sysStr, 10);
    const dia = parseInt(diaStr, 10);

    if (!Number.isFinite(sys) || !Number.isFinite(dia) || sys < 1 || dia < 1) {
      setError(t('dashboard.blood-pressure-add-error-positive-integers'));
      return;
    }

    if (!isPlausibleBloodPressure(sys, dia)) {
      setRangeWarning(true);
      return;
    }

    await doSave(sys, dia);
  };

  const handleConfirmDespiteRange = () => {
    const sysStr = onlyDigits(systolic);
    const diaStr = onlyDigits(diastolic);
    const sys = parseInt(sysStr, 10);
    const dia = parseInt(diaStr, 10);
    if (!sysStr || !diaStr || !Number.isFinite(sys) || !Number.isFinite(dia)) return;
    void doSave(sys, dia);
  };

  const sysStr = onlyDigits(systolic);
  const diaStr = onlyDigits(diastolic);
  const sysNum = sysStr ? parseInt(sysStr, 10) : NaN;
  const diaNum = diaStr ? parseInt(diaStr, 10) : NaN;
  const showRangeAlert =
    rangeWarning &&
    Number.isFinite(sysNum) &&
    Number.isFinite(diaNum) &&
    !isPlausibleBloodPressure(sysNum, diaNum);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('dashboard.blood-pressure-add-modal-title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}
        {showRangeAlert && (
          <Alert variant='warning' className='small'>
            <p className='fw-bold mb-2'>{t('dashboard.blood-pressure-add-range-alert-title')}</p>
            <ul className='mb-2 ps-3'>
              <li>
                {t('dashboard.blood-pressure-add-range-alert-sys', {
                  min: BP_SYSTOLIC_MIN,
                  max: BP_SYSTOLIC_MAX,
                })}
              </li>
              <li>
                {t('dashboard.blood-pressure-add-range-alert-dia', {
                  min: BP_DIASTOLIC_MIN,
                  max: BP_DIASTOLIC_MAX,
                })}
              </li>
            </ul>
            <p className='mb-2'>{t('dashboard.blood-pressure-add-range-alert-footer')}</p>
            <div className='d-flex flex-wrap gap-2'>
              <Button variant='outline-secondary' size='sm' onClick={() => setRangeWarning(false)}>
                {t('dashboard.blood-pressure-add-correct-data')}
              </Button>
              <Button
                variant='warning'
                size='sm'
                onClick={handleConfirmDespiteRange}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner animation='border' size='sm' />
                ) : (
                  t('dashboard.blood-pressure-add-confirm-save')
                )}
              </Button>
            </div>
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <Form.Group className='mb-3'>
            <Form.Label>{t('dashboard.blood-pressure-add-date-label')}</Form.Label>
            <Form.Control type='text' value={dateDisplay} readOnly className='bg-light' />
          </Form.Group>
          <Form.Group className='mb-2'>
            <Form.Label>{t('dashboard.blood-pressure-add-sys-label')}</Form.Label>
            <Form.Control
              type='text'
              inputMode='numeric'
              autoComplete='off'
              maxLength={4}
              value={systolic}
              onChange={(e) => setSystolic(onlyDigits(e.target.value))}
              placeholder={t('dashboard.blood-pressure-add-sys-placeholder')}
              required
            />
            <Form.Text className='text-muted'>
              {t('dashboard.blood-pressure-add-sys-hint', {
                min: BP_SYSTOLIC_MIN,
                max: BP_SYSTOLIC_MAX,
              })}
            </Form.Text>
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>{t('dashboard.blood-pressure-add-dia-label')}</Form.Label>
            <Form.Control
              type='text'
              inputMode='numeric'
              autoComplete='off'
              maxLength={4}
              value={diastolic}
              onChange={(e) => setDiastolic(onlyDigits(e.target.value))}
              placeholder={t('dashboard.blood-pressure-add-dia-placeholder')}
              required
            />
            <Form.Text className='text-muted'>
              {t('dashboard.blood-pressure-add-dia-hint', {
                min: BP_DIASTOLIC_MIN,
                max: BP_DIASTOLIC_MAX,
              })}
            </Form.Text>
          </Form.Group>
          <div className='d-flex justify-content-end gap-2'>
            <Button variant='secondary' onClick={onHide}>
              {t('dashboard.blood-pressure-add-cancel')}
            </Button>
            <Button variant='logo-green' type='submit' disabled={isLoading}>
              {isLoading ? (
                <Spinner animation='border' size='sm' />
              ) : (
                t('dashboard.blood-pressure-add-save')
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
